import { useEffect, useState } from 'react';
import { Box, ButtonBase, Collapse, Divider, Typography } from '@mui/material';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface ForecastResponse {
  city: { timezone: number };
  list: {
    dt: number;
    pop: number;
    rain?: { '3h'?: number };
  }[];
}

interface RainBlock {
  time: string;
  endTime: string;
  chance: number;
  mm: number;
}

const WET_CHANCE = 30;
const OPEN_KEY = 'hub.rainOpen';
const BLOCK_SECONDS = 3 * 60 * 60;

// OpenWeather's free forecast is 3-hour blocks in UTC. Shifting by the
// location's offset and reading UTC fields gives the local wall-clock time.
const toLocal = (unixSeconds: number, offsetSeconds: number) =>
  new Date((unixSeconds + offsetSeconds) * 1000);

const hhmm = (date: Date) =>
  `${String(date.getUTCHours()).padStart(2, '0')}:00`;

const todaysBlocks = (forecast: ForecastResponse): RainBlock[] => {
  const offset = forecast.city.timezone;
  const today = toLocal(Date.now() / 1000, offset).toISOString().slice(0, 10);

  return forecast.list
    .filter((step) => toLocal(step.dt, offset).toISOString().slice(0, 10) === today)
    .map((step) => ({
      time: hhmm(toLocal(step.dt, offset)),
      endTime: hhmm(toLocal(step.dt + BLOCK_SECONDS, offset)),
      chance: Math.round(step.pop * 100),
      mm: Math.round((step.rain?.['3h'] ?? 0) * 10) / 10,
    }));
};

const summarise = (blocks: RainBlock[]) => {
  const wet = blocks.filter((block) => block.chance >= WET_CHANCE);
  if (wet.length === 0) return 'Dry for the rest of today';

  const heaviest = Math.max(...wet.map((block) => block.mm));
  const range = `${wet[0].time}–${wet[wet.length - 1].endTime}`;
  return heaviest > 0
    ? `Rain likely ${range} · up to ${heaviest}mm`
    : `Showers possible ${range}`;
};

const RainToday = () => {
  const [blocks, setBlocks] = useState<RainBlock[] | null>(null);
  // Collapsed by default to keep the card short; remembered per browser.
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setOpen((was) => {
      try {
        localStorage.setItem(OPEN_KEY, was ? '0' : '1');
      } catch {}
      return !was;
    });
  };

  useEffect(() => {
    const fetchForecast = async () => {
      try {
        const res = await fetch(
          `https://api.openweathermap.org/data/2.5/forecast?lat=52.8408&lon=-6.9261&units=metric&appid=${process.env.REACT_APP_OPENWEATHER_KEY}`
        );
        if (!res.ok) throw new Error(`Forecast request failed (${res.status})`);
        setBlocks(todaysBlocks(await res.json()));
      } catch (err) {
        console.error(err);
      }
    };

    fetchForecast();
  }, []);

  // Late at night there may be no blocks left for today; hide rather than
  // show an empty strip.
  if (!blocks || blocks.length === 0) return null;

  return (
    <Box>
      <Divider sx={{ my: { xs: 1.5, md: 2 } }} />

      <ButtonBase
        onClick={toggle}
        aria-expanded={open}
        aria-controls="rain-today-bars"
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          py: 0.5,
          px: 0.5,
          mx: -0.5,
          borderRadius: 1.5,
          textAlign: 'left',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <WaterDropOutlinedIcon sx={{ fontSize: 16, color: '#38bdf8' }} />
          <Typography variant="body2" fontWeight={700}>
            Rain today
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" align="right">
            {summarise(blocks)}
          </Typography>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: 'text.secondary',
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
      </ButtonBase>

      <Collapse in={open} id="rain-today-bars">
        <Box
          sx={{
            pt: { xs: 1, md: 1.5 },
            display: 'grid',
            gridTemplateColumns: `repeat(${blocks.length}, minmax(0, 1fr))`,
            gap: 1,
          }}
        >
          {blocks.map((block) => (
            <Box
              key={block.time}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 14,
                  height: { xs: 32, md: 44 },
                  borderRadius: 7,
                  bgcolor: 'action.hover',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: `${Math.max(block.chance, 4)}%`,
                    borderRadius: 7,
                    background: 'linear-gradient(180deg, #38bdf8, #8b5cf6)',
                    opacity: block.chance ? 1 : 0.25,
                  }}
                />
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: 13 }}>
                {block.chance}%
              </Typography>
              <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                {block.time}
              </Typography>
              <Typography
                variant="caption"
                lineHeight={1.2}
                sx={{
                  color: block.mm
                    ? (theme) => (theme.palette.mode === 'dark' ? '#7dd3fc' : '#0284c7')
                    : 'text.disabled',
                }}
              >
                {block.mm ? `${block.mm}mm` : '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RainToday;
